import { useState, type CSSProperties, type PointerEventHandler, type SVGProps } from "react";

export type C1_ResizeGrabDirection = "top" | "right" | "bottom" | "left";

const pointsByDirection: Record<C1_ResizeGrabDirection, string> = {
  top: "0,-12 -10,8 10,8",
  right: "12,0 -8,-10 -8,10",
  bottom: "0,12 -10,-8 10,-8",
  left: "-12,0 8,-10 8,10",
};

export function C1_ResizeGrabShape({
  direction,
  active = false,
  bidirectional = false,
}: {
  direction: C1_ResizeGrabDirection;
  active?: boolean;
  bidirectional?: boolean;
}) {
  if (bidirectional) {
    const vertical = direction === "top" || direction === "bottom";
    const arrows = vertical
      ? ["0,-12 -7,-2 7,-2", "0,12 -7,2 7,2"]
      : ["-12,0 -2,-7 -2,7", "12,0 2,-7 2,7"];
    return (
      <g fill="white" stroke="var(--primaryButtonBackground, var(--sea))" strokeWidth={2}
        strokeLinejoin="round" pointerEvents="none">
        {arrows.map(points => <polygon key={points} points={points} />)}
      </g>
    );
  }
  return (
    <polygon
      points={pointsByDirection[direction]}
      fill={active ? "var(--primaryButtonBackground, var(--sea))" : "white"}
      stroke="var(--primaryButtonBackground, var(--sea))"
      strokeWidth={2}
      strokeLinejoin="round"
      pointerEvents="none"
    />
  );
}

export function C1_SvgResizeGrab({
  direction,
  bidirectional = false,
  x,
  y,
  active = false,
  hitSize = 30,
  hitCrossSize = 44,
  sizeScale = 1,
  onPointerDown,
  onPointerEnter,
  onPointerLeave,
  ...groupProps
}: {
  direction: C1_ResizeGrabDirection;
  bidirectional?: boolean;
  x: number;
  y: number;
  active?: boolean;
  hitSize?: number;
  hitCrossSize?: number;
  sizeScale?: number;
  onPointerDown?: PointerEventHandler<SVGGElement>;
  onPointerEnter?: PointerEventHandler<SVGGElement>;
  onPointerLeave?: PointerEventHandler<SVGGElement>;
} & Omit<SVGProps<SVGGElement>, "onPointerDown" | "onPointerEnter" | "onPointerLeave">) {
  const [hovered, setHovered] = useState(false);
  const vertical = direction === "top" || direction === "bottom";
  const hitWidth = vertical ? hitCrossSize : hitSize;
  const hitHeight = vertical ? hitSize : hitCrossSize;
  const visuallyActive = active || hovered;

  return (
    <g
      {...groupProps}
      transform={`translate(${x} ${y}) scale(${sizeScale})`}
      pointerEvents="all"
      onPointerDown={onPointerDown}
      onPointerEnter={(event) => {
        setHovered(true);
        onPointerEnter?.(event);
      }}
      onPointerLeave={(event) => {
        setHovered(false);
        onPointerLeave?.(event);
      }}
    >
      <rect
        x={-hitWidth / 2}
        y={-hitHeight / 2}
        width={hitWidth}
        height={hitHeight}
        fill="transparent"
        stroke="none"
        pointerEvents="all"
      />
      <g
        style={{
          transform: `scale(${!bidirectional && visuallyActive ? 1.5 : 1})`,
          transformOrigin: "0 0",
          transition: "transform 160ms ease",
          animation: !bidirectional && visuallyActive ? "C1_svgResizeGrabBounce 280ms cubic-bezier(0.2, 0.8, 0.3, 1)" : undefined,
          filter: bidirectional && visuallyActive ? "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.45))" : undefined,
          pointerEvents: "none",
        }}
      >
        <C1_ResizeGrabShape direction={direction} active={visuallyActive} bidirectional={bidirectional} />
      </g>
    </g>
  );
}

export function C1_ResizeGrabIcon({
  direction,
  active = false,
  position,
  sizeScale = 1,
}: {
  direction: C1_ResizeGrabDirection;
  active?: boolean;
  position?: Pick<CSSProperties, "left" | "top">;
  sizeScale?: number;
}) {
  const style: CSSProperties = {
    position: "absolute",
    left: position?.left ?? "50%",
    top: position?.top ?? "50%",
    width: 30 * sizeScale,
    height: 30 * sizeScale,
    transform: `translate(-50%, -50%)${active ? " scale(1.5)" : " scale(1)"}`,
    transition: "transform 160ms ease",
    animation: active ? "C1_resizeGrabBounce 280ms cubic-bezier(0.2, 0.8, 0.3, 1)" : undefined,
    pointerEvents: "none",
    overflow: "visible",
  };

  return (
    <svg viewBox="-15 -15 30 30" style={style} aria-hidden="true">
      <C1_ResizeGrabShape direction={direction} active={active} />
    </svg>
  );
}
